<template>
  <v-sheet elevation="2" class="px-12 py-8 rounded white">
    <div class="mb-6">
      <p class="h6 pa-0 mb-0">
        {{ $t('settings.situation_types') }}
      </p>
      <p
        class="mt-2 pa-0 grey--text text--darken-2"
        v-html="$t('site_settings.situationTypes_subtitle')"
      />
    </div>

    <div v-for="type in types" :key="type">
      <p v-if="shouldShowType(type)" class="grey--text mt-6 overline-rever">
        {{ $t(`quick_action.situation_types.${type}`) }}
        <v-divider class="me-12 my-3" />
      </p>
      <div v-for="situation in filterSituationByType(type)" :key="situation.id">
        <v-hover v-slot:default="{ hover }">
          <div
            v-if="shouldShowSituationType(situation)"
            class="d-flex py-2 px-3 items-start justify-space-between mb-3"
            :key="situation.id"
            :class="{
              hover: hover,
              grey: hover,
              'lighten-5': hover,
            }"
          >
            <p
              class="ma-0"
              data-testid="situationType-name"
              :class="{ 'grey--text': shouldDisableSituationType(situation) }"
            >
              {{ situation.text }}
            </p>
            <v-tooltip
              top
              :key="situation.id"
              :disabled="!shouldDisableSituationType(situation)"
              ><template v-slot:activator="{ on }">
                <div v-on="on">
                  <v-switch
                    class="ma-0 pa-0 d-flex items-center"
                    hide-details
                    :id="situation.id"
                    :input-value="situationTypeIsVisible(situation)"
                    :disabled="shouldDisableSituationType(situation)"
                    @click.native="setRule(situation)"
                  ></v-switch>
                </div>
              </template>
              {{ $t('site_settings.situation_types_tooltip') }}
            </v-tooltip>
          </div>
        </v-hover>
      </div>
    </div>
  </v-sheet>
</template>
<script>
import mixin from '@/mixins/mixin.vue'
import { mapActions } from 'vuex'
import _ from 'lodash'

export default {
  name: 'SituationTypes',
  mixins: [mixin],

  props: {
    siteId: {
      type: String,
      required: true,
    },
    organizationId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      situationTypes: [],
      siteRules: [],
      organizationRules: [],
    }
  },

  async mounted() {
    const catalog = await this.getSituationTypesCatalog()

    await this.getSiteRules()
    await this.getOrganizationRules()
    this.situationTypes = this.formatSituationTypes(catalog)
  },

  computed: {
    types() {
      return [...new Set(this.situationTypes.map(situation => situation.type))]
    },
  },

  methods: {
    ...mapActions({
      fetchSiteRules: 'situationTypes/getSiteRules',
      createRule: 'situationTypes/createSiteRule',
      updateRule: 'situationTypes/updateSiteRule',
      getCatalog: 'situationTypes/getOrganizationCatalog',
      fetchOrganizationRules: 'situationTypes/getOrganizationRules',
    }),

    async getSituationTypesCatalog() {
      try {
        const response = await this.getCatalog({
          organizationId: this.organizationId,
        })

        return response
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async getSiteRules() {
      try {
        const response = await this.fetchSiteRules({
          organizationId: this.organizationId,
          siteId: this.siteId,
        })
        this.siteRules = response.data
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async getOrganizationRules() {
      try {
        const response = await this.fetchOrganizationRules({
          organizationId: this.organizationId,
        })
        this.organizationRules = response.data
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    formatSituationTypes(catalog) {
      const situations = catalog.map(situation => ({
        ...situation,
        text: this.$t(
          `quick_action.situation_types.situations.${situation.name}`
        ),
        rule: this.getSituationTypeRule(situation),
        loading: false,
      }))

      return _.orderBy(situations, 'type', 'asc')
    },

    getSituationTypeRule(situationType) {
      const organizationRule = this.organizationRules.find(
        rule => rule.situationType === situationType.id
      )
      if (organizationRule) return organizationRule

      const siteRule = this.siteRules.find(
        rule => rule.situationType === situationType.id
      )

      if (siteRule) return siteRule
      return { isVisible: situationType.isDefault }
    },

    setRule(situationType) {
      const payload = {
        organizationId: this.organizationId,
        siteId: this.siteId,
        rule: {
          isVisible: !situationType?.rule?.isVisible,
        },
      }

      if (situationType?.rule?.id) {
        payload.ruleId = situationType.rule.id
        return this.updateSituationTypeRule(payload, situationType)
      } else {
        payload.rule.situationType = situationType.id
        return this.createSituationTypeRule(payload, situationType)
      }
    },

    async updateSituationTypeRule(payload, situationType) {
      situationType.loading = true
      try {
        await this.updateRule(payload)
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        situationType.loading = false
        this.getSiteRules()
      }
    },

    async createSituationTypeRule(payload, situationType) {
      situationType.loading = true
      try {
        await this.createRule(payload)
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        situationType.loading = false
        this.getSiteRules()
      }
    },

    situationTypeIsVisible(situationType) {
      return situationType?.rule?.isVisible ?? false
    },

    filterSituationByType(type) {
      return this.situationTypes.filter(situation => situation.type === type)
    },

    shouldDisableSituationType(situationType) {
      const rule = this.organizationRules.find(
        rule => rule.situationType === situationType.id
      )
      return Boolean(situationType.loading || rule)
    },

    shouldShowSituationType(situationType) {
      const rule = this.organizationRules.find(
        rule => rule.situationType === situationType.id
      )

      return rule?.isVisible ?? true
    },

    shouldShowType(type) {
      return this.filterSituationByType(type).filter(
        this.shouldShowSituationType
      ).length
    },
  },
}
</script>
<style lang="scss" scoped>
.hover {
  border-radius: 4px;
}
</style>
